import {createContext, useCallback, useContext, useMemo, useState} from 'react';

const JobContext = createContext(null);

const STORAGE_KEY = 'nxs_jobs_context';

function _validateSelection(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

    const result = {};
    for (const [key, value] of Object.entries(input)) {
        const cid = Number(key);
        if (isNaN(cid)) continue;
        if (!Array.isArray(value)) return {};
        const ints = value.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(n => !isNaN(n));
        if (ints.length) result[cid] = ints;
    }
    return result;
}

function _readStorage() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return _validateSelection(parsed);
    } catch {
        return {};
    }
}

/**
 * Forgets the stored selection, so the next page load starts at All Jobs.
 *
 * Called by AuthProvider on both logout and login rather than from inside this provider, for two reasons.
 * The selection lives in sessionStorage, which survives a logout — logging out only clears the *session*
 * from localStorage — so without this the next person to log in at that tab inherits whatever the last one
 * had ticked. For an admin that is another customer's job references on screen before they have chosen
 * anything, and for anyone it is a dashboard scoped to a selection they did not make and did not see being
 * made. And the provider cannot do it itself: it is mounted inside AuthProvider, so it has no way to hear
 * about a login, while logout replaces the document entirely and tears this provider's state down with it.
 *
 * A plain function rather than part of the context value because the caller is above the provider in the
 * tree and cannot use the hook.
 */
export function clearStoredJobSelection() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // A browser with storage denied has nothing stored to clear, and a logout must not fail on it.
    }
}

export const JobContextProvider = ({children}) => {
    const [selection, setSelectionState] = useState(_readStorage);
    const [selectorEnabled, setSelectorEnabled] = useState(false);
    /*
     * Every job the selector offers, and whether that list is still on its way.
     *
     * Held here rather than only in the header because it is not only the selector's business any more.
     * A widget reporting on "all jobs" has to know which jobs those are: with nothing selected there is
     * no cid and no job id to send, and a count with neither is a count of every company in ArkBridge —
     * including media that was never onboarded as a job at all. So the aggregate is taken over this list
     * instead, and the number the dashboard shows is the sum of the jobs the selector beside it lists.
     *
     * The header is still the one place that fetches it (it is the component that owns the selector, and
     * the two roles fetch it from different endpoints); this is where it is published so that anything
     * else on the page can read it. Written through {@link setJobGroups} — see the pending contract there.
     */
    const [jobGroups, setJobGroupsState] = useState([]);
    /*
     * Pending from the first render, before anyone has asked for the list.
     *
     * Because someone always does: the header fetches it on mount, and it resolves — to jobs, or to none —
     * as soon as that request answers. Starting at false instead makes the first render of every page look
     * like a settled empty list, so a consumer scoping itself by these jobs concludes there are none, shows
     * that, and corrects itself two renders later. A card flashing 'nothing onboarded' and then figures is
     * worse than one that says it is loading, which is what it is.
     */
    const [jobGroupsPending, setJobGroupsPending] = useState(true);
    /*
     * A selection is any number of jobs, under any number of customers.
     *
     * It was one job, silently truncated to the first of whatever it was given, and the selector offered no
     * way to ask for more. What was asked for is a company checkbox that takes every job beneath it, jobs
     * pickable one at a time under a company, and a selection free to span companies — so the truncation is
     * gone and the shape the context always carried, `{[cid]: jobId[]}`, is now used as it reads.
     *
     * Every consumer was already written for it: the dashboard widgets send `selectedJobIds` with
     * `selectedCid`, and `vi.JobID in (…)` is scoped by job and not by company, so a set of jobs spanning two
     * customers is one query rather than one per customer. `selectedCid` stays undefined unless exactly one
     * customer is in the selection, which is what the SaaS `cid` means — see the comment on it below.
     */
    const isMulti = true;

    const setSelection = useCallback((input) => {
        const validated = _validateSelection(input);
        setSelectionState(validated);
        if (Object.keys(validated).length) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(validated));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    /**
     * Publishes the selector's groups, or announces that they are coming.
     *
     * `undefined` means "a fetch is in flight": the two are deliberately one function, because a consumer
     * scoping itself by these jobs has to be able to tell "no jobs" from "not yet". Without the
     * distinction the dashboard fires its thirteen counts unscoped on mount, then again scoped a moment
     * later when the list lands — twenty-six queries for one page, and whichever batch answers first is
     * what the reader sees.
     *
     * Anything that is not an array clears the list rather than throwing, so a failed fetch resolves the
     * pending state instead of leaving the page waiting on a request that is never coming back.
     */
    const setJobGroups = useCallback((groups) => {
        if (groups === undefined) {
            setJobGroupsPending(true);
            return;
        }
        setJobGroupsState(Array.isArray(groups) ? groups : []);
        setJobGroupsPending(false);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectionState({});
        sessionStorage.removeItem(STORAGE_KEY);
    }, []);

    const selectedCids = useMemo(() => Object.keys(selection).map(Number), [selection]);
    const selectedJobIds = useMemo(() => Object.values(selection).flat(), [selection]);
    const hasSelection = selectedJobIds.length > 0;
    /*
     * The single customer id to forward as the SaaS `cid` (= target_company_id) when exactly one
     * customer is selected. undefined otherwise → callers fall back to _deriveCID() (today's behaviour).
     *
     * Undefined is the honest answer for a selection spanning customers, and it costs nothing: what bounds
     * such a request is `selectedJobIds`, and a JobId identifies one job of one company, so the rows come
     * back scoped to exactly the jobs that were ticked.
     */
    const selectedCid = selectedCids.length === 1 ? selectedCids[0] : undefined;

    /*
     * Every job id in the selector, flattened across customers — the scope of "All Jobs".
     *
     * Deduplicated and sorted so it is a stable value: consumers spread it into a request and join it
     * into a useEffect dependency key, and a list whose order moved with the group ordering would re-fire
     * those requests for no reason.
     *
     * Deduplication is belt and braces rather than load-bearing. A JobId is the primary key of noah.Job
     * and identifies one job of one company — which is what makes this list safe to send with no cid at
     * all: `vi.JobID in (…)` cannot pick up another customer's media, so the aggregate is one query
     * rather than one per customer. Ids are filtered to > 0 for the reason jobGroupsBuilder rejects them:
     * 0 is Django's unset default for tapeark_job_id and matches no media.
     */
    const allJobIds = useMemo(() => {
        const ids = new Set();
        jobGroups.forEach(group => (Array.isArray(group?.items) ? group.items : []).forEach(item => {
            const id = Number(item?.key);
            if (id > 0) ids.add(id);
        }));
        return [...ids].sort((a, b) => a - b);
    }, [jobGroups]);

    const value = useMemo(() => ({
        selection,
        setSelection,
        clearSelection,
        isMulti,
        selectorEnabled,
        setSelectorEnabled,
        selectedCids,
        selectedJobIds,
        selectedCid,
        hasSelection,
        jobGroups,
        setJobGroups,
        jobGroupsPending,
        allJobIds,
    }), [selection, setSelection, clearSelection, isMulti, selectorEnabled, selectedCids, selectedJobIds, selectedCid, hasSelection, jobGroups, setJobGroups, jobGroupsPending, allJobIds]);

    return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
};

export function useJobContext() {
    const context = useContext(JobContext);
    if (!context) throw new Error('useJobContext must be used within a JobContextProvider');
    return context;
}
